import styles from "./testCaseTemplates.module.scss";
import {
  TestCase,
  TestCaseTemplate
} from "../../../types/testCaseTemplateTypes";
import { Accordion } from "../../../shared/components/accordion/accordion";
import { TestCaseTemplateItem } from "../testCaseTemplateItem/testCaseTemplateItem";
import { TestCaseRegularItem } from "../testCaseRegularItem/testCaseRegularItem";
import {
  deleteSelectedTestCase,
  deleteSelectedTestCaseTemplate,
  setSelectedTestCase,
  setSelectedTestCaseTemplate
} from "../../../store/slices/testCaseTemplateSlice";
import {
  useEffect,
  useState
} from "react";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../store";
import { TestCaseTemplateModal } from "../../testCaseTemplateModal/testCaseTemplateModal";
import { TestCaseModal } from "../../testCaseModal/testCaseModal";

type TestCaseTemplatesProps = {
  testCaseTemplates: Array<TestCaseTemplate>;
  isActive: boolean;
  refetch: () => void;
  needFindTemplate: () => void;
}

export const TestCaseTemplates = ({ testCaseTemplates, refetch, isActive, needFindTemplate }: TestCaseTemplatesProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);
  const selectedTestCaseTemplate = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCaseTemplate);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const [activeIndex, setActiveIndex] = useState<number>()

  const [showTestCaseTemplateModal, setShowTestCaseTemplateModal] = useState<boolean>(false)
  const [editTestCaseTemplate, setEditTestCaseTemplate] = useState<TestCaseTemplate>()

  const [showTestCaseModal, setShowTestCaseModal] = useState<boolean>(false)
  const [editTestCase, setEditTestCase] = useState<TestCase>()

  useEffect(() => {
    if (isActive) {
      setDefaultTestCase();
    } else {
      setActiveIndex(undefined);
    }
  }, [testCaseTemplates, isActive]);

  const setDefaultTestCase = (): void => {
    if (!!testCaseTemplates.length) {
      const findIndexTemplate = testCaseTemplates.findIndex(item => item.id === selectedTestCaseTemplate.id);
      if (findIndexTemplate === -1 && !!selectedTestCaseTemplate.id) {
        needFindTemplate();
      } else if (!!selectedTestCase.id) {
        let newSelectedTestCase: TestCase | undefined;
        const newSelectedTestCaseTemplateIndex = testCaseTemplates.findIndex(item => {
          newSelectedTestCase = item.childTestCases.find(child => child.id === selectedTestCase.id);
          return !!newSelectedTestCase
        });
        if (!!newSelectedTestCase) {
          setActiveIndex(newSelectedTestCaseTemplateIndex);
          dispatch(setSelectedTestCase({
            testCase: newSelectedTestCase,
            template: testCaseTemplates[newSelectedTestCaseTemplateIndex],
            selectedVariables: selectedVariables
          }));
        } else {
          if (findIndexTemplate > -1 && testCaseTemplates[findIndexTemplate].childTestCases.length !== 0) {
            setActiveIndex(findIndexTemplate);
            dispatch(setSelectedTestCaseTemplate(testCaseTemplates[findIndexTemplate]));
            dispatch(deleteSelectedTestCase());
          } else if (testCaseTemplates.length === 1 && testCaseTemplates[0].childTestCases.length !== 0) {
            setActiveIndex(0);
          } else {
            setActiveIndex(undefined);
          }
        }
      } else {
        if (findIndexTemplate > -1) {
          if (testCaseTemplates[findIndexTemplate].childTestCases.length !== 0) {
            setActiveIndex(findIndexTemplate);
          } else {
            setActiveIndex(undefined);
          }
          dispatch(setSelectedTestCaseTemplate(testCaseTemplates[findIndexTemplate]));
        } else if (testCaseTemplates.length === 1 && testCaseTemplates[0].childTestCases.length !== 0) {
          setActiveIndex(0);
        } else {
          setActiveIndex(undefined);
        }
      }
    }
  }

  const changeActiveTestCaseTemplate = (templateIndex: number): void => {
    if (templateIndex === activeIndex) {
      dispatch(deleteSelectedTestCaseTemplate());
      setActiveIndex(undefined);
    } else {
      setActiveIndex(templateIndex);
      dispatch(setSelectedTestCaseTemplate(testCaseTemplates[templateIndex]));
      dispatch(deleteSelectedTestCase());
    }
  }

  const editTemplateClick = (testCaseTemplate: TestCaseTemplate): void => {
    setEditTestCaseTemplate(testCaseTemplate);
    setShowTestCaseTemplateModal(true);
  }

  const editTestCaseClick = (testCaseTemplate: TestCaseTemplate, testCase: TestCase): void => {
    setEditTestCase(testCase);
    setEditTestCaseTemplate(testCaseTemplate)
    setShowTestCaseModal(true);
  }

  const copyTemplateClick = (testCaseTemplate: TestCaseTemplate): void => {
    const newTestCaseTemplate: TestCaseTemplate = {...testCaseTemplate};
    newTestCaseTemplate.id = undefined;
    newTestCaseTemplate.name = `${testCaseTemplate.name} - copy`;
    setEditTestCaseTemplate(newTestCaseTemplate);
    setShowTestCaseTemplateModal(true);
  }

  const copyTestCaseClick = (testCaseTemplate: TestCaseTemplate, testCase: TestCase): void => {
    const newTestCase: TestCase = {...testCase};
    newTestCase.id = undefined;
    newTestCase.name = `${testCase.name} - copy`;
    setEditTestCaseTemplate(testCaseTemplate);
    setEditTestCase(newTestCase);
    setShowTestCaseModal(true);
  }

  const addTestCaseClick = (testCaseTemplate: TestCaseTemplate): void => {
    setShowTestCaseModal(true);
    setEditTestCaseTemplate(testCaseTemplate);
  }

  const closeTestCaseModal = (isSave: boolean, createId?: number): void => {
    setShowTestCaseModal(false);
    setEditTestCaseTemplate(undefined);
    setEditTestCase(undefined);
    if (isSave) {
      if (!!createId) {
        const newSelectedTestCase = {...selectedTestCase};
        newSelectedTestCase.id = createId;
        dispatch(setSelectedTestCase({ testCase: newSelectedTestCase, template: selectedTestCaseTemplate, selectedVariables: selectedVariables }));
      }
      refetch();
    }
  }

  const closeTestCaseTemplateModal = (): void => {
    setShowTestCaseTemplateModal(false);
    setEditTestCaseTemplate(undefined);
  }

  const afterCreateTestCaseTemplate = (createId: number): void => {
    closeTestCaseTemplateModal();
    const newSelectedTestCaseTemplate = {...selectedTestCaseTemplate};
    newSelectedTestCaseTemplate.id = createId;
    dispatch(setSelectedTestCaseTemplate(newSelectedTestCaseTemplate));
    dispatch(deleteSelectedTestCase());
    refetch();
  }

  const afterUpdateTestCaseTemplate = (): void => {
    if (!!editTestCaseTemplate) {
      dispatch(setSelectedTestCaseTemplate(editTestCaseTemplate));
    }
    closeTestCaseTemplateModal();
    refetch();
  }

  const afterDeleteTestCaseTemplate = (): void => {
    dispatch(deleteSelectedTestCaseTemplate());
    closeTestCaseTemplateModal();
    refetch();
  }

  return (
    <>
      <div className={styles.container}>
        <Accordion activeClassName={styles.active}
                   activeIndex={activeIndex}
                   customClick={true}
                   data={testCaseTemplates.map((testCaseTemplate, indexTemplate) => {
                     return {
                       title: (
                         <TestCaseTemplateItem
                           key={indexTemplate}
                           testCaseTemplate={testCaseTemplate}
                           onClick={() => changeActiveTestCaseTemplate(indexTemplate)}
                           refetch={refetch}
                           arrowClass={styles.arrow}
                           onAddTestCaseClick={() => addTestCaseClick(testCaseTemplate)}
                           onEditClick={() => editTemplateClick(testCaseTemplate)}
                           onCopyClick={() => copyTemplateClick(testCaseTemplate)}/>
                       ),
                       child: (
                         <div className={styles.testCases}>
                           {testCaseTemplate.childTestCases?.map((testCase, indexTestCase) => (
                             <TestCaseRegularItem
                               key={indexTestCase}
                               testCase={testCase}
                               testCaseTemplate={testCaseTemplate}
                               refetch={refetch}
                               onEditClick={() => editTestCaseClick(testCaseTemplate, testCase)}
                               onCopyClick={() => copyTestCaseClick(testCaseTemplate, testCase)}/>
                           ))}
                         </div>
                       )
                     }
                   })}/>
      </div>
      {showTestCaseTemplateModal && (<TestCaseTemplateModal onClose={closeTestCaseTemplateModal}
                                                            onCreate={afterCreateTestCaseTemplate}
                                                            onUpdate={afterUpdateTestCaseTemplate}
                                                            onDelete={afterDeleteTestCaseTemplate}
                                                            testCaseTemplate={editTestCaseTemplate}/>)}
      {showTestCaseModal && editTestCaseTemplate && (<TestCaseModal onClose={closeTestCaseModal}
                                                                    testCaseTemplate={editTestCaseTemplate}
                                                                    testCase={editTestCase}/>)}
    </>

  );
}